import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';

type DashboardView = 'admin' | 'carrier' | 'customer' | 'shipper' | 'consignee';
type DateBasis = 'appointment' | 'created' | 'pickup' | 'delivery' | 'status';
type QueueKey = 'late' | 'due_today' | 'stale_status';
type DimensionKey = 'customers' | 'carriers' | 'shippers' | 'consignees';
type Tone = 'blue' | 'emerald' | 'amber' | 'rose' | 'violet' | 'slate';
type ThemeMode = 'light' | 'dark';
type ContactFilterParam = 'customer_contact_uuid' | 'carrier_contact_uuid' | 'shipper_contact_uuid' | 'consignee_contact_uuid';

interface DashboardMeta {
  as_of?: string;
  timezone?: string;
  dashboard_view?: DashboardView | string;
}

interface DashboardFilters {
  date_basis?: DateBasis | string;
  date_start?: string;
  date_end?: string;
  active_only?: boolean;
  include_cancelled?: boolean;
  stale_hours?: number;
  detail_limit?: number;
  lading_status?: string;
}

interface DashboardCards {
  active_work_orders?: number;
  late_appointments?: number;
  due_today?: number;
  delivered_today?: number;
  stale_status?: number;
  hot?: number;
  expedited?: number;
  cancelled_excluded?: number;
}

interface StatusCount {
  lading_status: string;
  count: number;
}

interface DashboardRow {
  lading_uuid?: string;
  alternate_reference?: string | null;
  lading_number?: string | null;
  pro_bill?: string | null;
  trip_number?: string | null;
  lading_status?: string | null;
  latest_status?: string | null;
  status_activity_at?: string | null;
  created?: string | null;
  load_start?: string | null;
  load_end?: string | null;
  appointment_date?: string | null;
  appointment_start?: string | null;
  appointment_end?: string | null;
  appointment_start_local?: string | null;
  appointment_end_local?: string | null;
  customer_name?: string | null;
  carrier_name?: string | null;
  shipper_name?: string | null;
  shipper_locality?: string | null;
  shipper_region?: string | null;
  consignee_name?: string | null;
  consignee_locality?: string | null;
  consignee_region?: string | null;
  hot?: boolean;
  expedited?: boolean;
  drop_trailer?: boolean;
  is_late_appointment?: boolean;
  is_due_today?: boolean;
  is_stale_status?: boolean;
}

interface DashboardSection {
  count: number;
  rows: DashboardRow[];
}

interface DimensionRow {
  uuid?: string;
  name: string;
  count: number;
  active_count: number;
  late_appointments: number;
  due_today: number;
  delivered_today: number;
}

interface DashboardSections {
  late?: DashboardSection;
  due_today?: DashboardSection;
  stale_status?: DashboardSection;
}

interface DashboardDimensions {
  customers?: DimensionRow[];
  carriers?: DimensionRow[];
  shippers?: DimensionRow[];
  consignees?: DimensionRow[];
}

interface LadingOperationsDashboardResponse {
  success?: boolean;
  meta?: DashboardMeta;
  filters?: DashboardFilters;
  cards?: DashboardCards;
  status_counts?: StatusCount[];
  sections?: DashboardSections;
  dimensions?: DashboardDimensions;
  warnings?: string[];
}

interface ApiMessage {
  name?: string;
  message?: string;
  code?: string;
}

interface FilterState {
  dashboard_view: DashboardView;
  date_basis: DateBasis;
  date_start: string;
  date_end: string;
  active_only: boolean;
  include_cancelled: boolean;
  stale_hours: number;
  detail_limit: number;
  lading_status: string;
  timezone: string;
}

interface KpiCard {
  key: string;
  label: string;
  value: number;
  caption: string;
  detail: string;
  tone: Tone;
  icon: string;
}

interface QueueTab {
  key: QueueKey;
  label: string;
  icon: string;
}

interface DimensionTab {
  key: DimensionKey;
  label: string;
}

interface ContactFilter {
  dimension: DimensionKey;
  paramName: ContactFilterParam;
  uuid: string;
  name: string;
}

@Component({
  selector: 'app-lading-operations-dashboard',
  templateUrl: './lading-operations-dashboard.component.html',
  styleUrls: ['./lading-operations-dashboard.component.scss']
})
export class LadingOperationsDashboardComponent implements OnInit {
  private readonly apiUrl = '/api/dashboard/lading_operations_dashboard.php';
  private readonly themeStorageKey = 'lading-operations-dashboard-theme';
  private readonly numberFormatter = new Intl.NumberFormat('en-US');
  private readonly dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  private readonly dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  private readonly dimensionParamMap: Record<DimensionKey, ContactFilterParam> = {
    customers: 'customer_contact_uuid',
    carriers: 'carrier_contact_uuid',
    shippers: 'shipper_contact_uuid',
    consignees: 'consignee_contact_uuid',
  };

  data: LadingOperationsDashboardResponse | null = null;
  loading = false;
  errorMessage = '';
  warnings: string[] = [];
  activeQueue: QueueKey = 'late';
  themeMode: ThemeMode = this.resolveInitialTheme();
  activeDimension: DimensionKey = 'customers';
  activeContactFilter: ContactFilter | null = null;

  filters: FilterState = {
    dashboard_view: 'admin',
    date_basis: 'appointment',
    date_start: this.formatDateInput(this.addDays(new Date(), -90)),
    date_end: this.formatDateInput(this.addDays(new Date(), 30)),
    active_only: true,
    include_cancelled: false,
    stale_hours: 24,
    detail_limit: 25,
    lading_status: 'all',
    timezone: this.detectTimezone(),
  };

  readonly dashboardViews: Array<{ value: DashboardView; label: string }> = [
    { value: 'admin', label: 'Admin' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'customer', label: 'Customer' },
    { value: 'shipper', label: 'Shipper' },
    { value: 'consignee', label: 'Consignee' },
  ];

  readonly dateBasisOptions: Array<{ value: DateBasis; label: string }> = [
    { value: 'appointment', label: 'Appointment' },
    { value: 'created', label: 'Created' },
    { value: 'pickup', label: 'Pickup' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'status', label: 'Status' },
  ];

  readonly rowLimitOptions: Array<{ value: number; label: string }> = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  readonly queueTabs: QueueTab[] = [
    { key: 'late', label: 'Late', icon: 'fa-triangle-exclamation' },
    { key: 'due_today', label: 'Due Today', icon: 'fa-calendar-day' },
    { key: 'stale_status', label: 'Stale', icon: 'fa-clock-rotate-left' },
  ];

  readonly dimensionTabs: DimensionTab[] = [
    { key: 'customers', label: 'Customers' },
    { key: 'carriers', label: 'Carriers' },
    { key: 'shippers', label: 'Shippers' },
    { key: 'consignees', label: 'Consignees' },
  ];

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.refreshDashboard();
  }

  get themeLabel(): string {
    return this.themeMode === 'dark' ? 'Dark' : 'Light';
  }

  get themeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-moon' : 'fa-sun';
  }

  get kpiCards(): KpiCard[] {
    const active = this.cardValue('active_work_orders');
    const late = this.cardValue('late_appointments');
    const due = this.cardValue('due_today');
    const stale = this.cardValue('stale_status');
    const delivered = this.cardValue('delivered_today');
    const hot = this.cardValue('hot');
    const expedited = this.cardValue('expedited');

    return [
      {
        key: 'active',
        label: 'Active Orders',
        value: active,
        caption: 'Live workload',
        detail: `${this.percentText(late, active)} late`,
        tone: 'blue',
        icon: 'fa-route',
      },
      {
        key: 'late',
        label: 'Late Appointments',
        value: late,
        caption: 'Needs recovery',
        detail: `${this.percentText(late, active)} of active`,
        tone: 'rose',
        icon: 'fa-triangle-exclamation',
      },
      {
        key: 'due',
        label: 'Due Today',
        value: due,
        caption: 'Today window',
        detail: `${delivered.toLocaleString()} delivered today`,
        tone: 'amber',
        icon: 'fa-calendar-check',
      },
      {
        key: 'stale',
        label: 'Stale Status',
        value: stale,
        caption: `${this.filters.stale_hours}h threshold`,
        detail: `${this.percentText(stale, active)} of active`,
        tone: 'violet',
        icon: 'fa-clock-rotate-left',
      },
      {
        key: 'priority',
        label: 'Priority Flags',
        value: hot + expedited,
        caption: 'Hot and expedited',
        detail: `${hot.toLocaleString()} hot / ${expedited.toLocaleString()} expedited`,
        tone: 'emerald',
        icon: 'fa-bolt',
      },
    ];
  }

  get statusCounts(): StatusCount[] {
    return this.data?.status_counts ?? [];
  }

  get statusTotal(): number {
    return this.statusCounts.reduce((sum, row) => sum + row.count, 0);
  }

  get maxStatusCount(): number {
    return this.statusCounts.reduce((max, row) => Math.max(max, row.count), 0);
  }

  get activeRows(): DashboardRow[] {
    return this.getQueueSection(this.activeQueue).rows;
  }

  get activeQueueCount(): number {
    return this.getQueueSection(this.activeQueue).count;
  }

  get activeDimensionRows(): DimensionRow[] {
    return this.getDimensionRows(this.activeDimension);
  }

  get maxDimensionCount(): number {
    return this.activeDimensionRows.reduce((max, row) => Math.max(max, row.count), 0);
  }

  get availableStatuses(): string[] {
    return this.statusCounts.map(row => row.lading_status);
  }

  get statusFilterOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'all', label: 'All active' },
      ...this.availableStatuses.map(status => ({ value: status, label: status })),
    ];
  }

  get asOfLabel(): string {
    return this.formatDateTime(this.data?.meta?.as_of ?? '');
  }

  get filterWindowLabel(): string {
    const start = this.datePart(this.data?.filters?.date_start ?? this.filters.date_start);
    const end = this.datePart(this.data?.filters?.date_end ?? this.filters.date_end);
    return start && end ? `${start} to ${end}` : 'Current window';
  }

  refreshDashboard(): void {
    this.loading = true;
    this.errorMessage = '';

    this.http.get<LadingOperationsDashboardResponse | ApiMessage[]>(this.apiUrl, { params: this.buildParams() }).subscribe({
      next: response => {
        this.loading = false;
        this.handleResponse(response);
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.errorMessage = error.error?.[0]?.message || error.error?.message || error.message || 'Unable to load lading operations.';
      },
    });
  }

  resetFilters(): void {
    this.activeContactFilter = null;
    this.filters = {
      dashboard_view: 'admin',
      date_basis: 'appointment',
      date_start: this.formatDateInput(this.addDays(new Date(), -90)),
      date_end: this.formatDateInput(this.addDays(new Date(), 30)),
      active_only: true,
      include_cancelled: false,
      stale_hours: 24,
      detail_limit: 25,
      lading_status: 'all',
      timezone: this.detectTimezone(),
    };
    this.refreshDashboard();
  }


  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  selectQueue(key: QueueKey): void {
    this.activeQueue = key;
  }

  selectDimension(key: DimensionKey): void {
    this.activeDimension = key;
  }

  applyDimensionFilter(row: DimensionRow): void {
    if (!row.uuid) {
      return;
    }

    this.activeContactFilter = {
      dimension: this.activeDimension,
      paramName: this.dimensionParamMap[this.activeDimension],
      uuid: row.uuid,
      name: row.name,
    };
    this.refreshDashboard();
  }

  clearContactFilter(): void {
    this.activeContactFilter = null;
    this.refreshDashboard();
  }

  openWorkOrder(row: DashboardRow): void {
    if (!row.lading_uuid) {
      return;
    }

    this.router.navigate(['/work_order_details', row.lading_uuid]);
  }

  getQueueCount(key: QueueKey): number {
    return this.getQueueSection(key).count;
  }

  getQueueLabel(key: QueueKey): string {
    const tab = this.queueTabs.find(item => item.key === key);
    return tab?.label ?? 'Queue';
  }

  getQueueSection(key: QueueKey): DashboardSection {
    const fallback: DashboardSection = { count: 0, rows: [] };
    return this.data?.sections?.[key] ?? fallback;
  }

  getDimensionRows(key: DimensionKey): DimensionRow[] {
    return this.data?.dimensions?.[key] ?? [];
  }

  cardValue(key: keyof DashboardCards): number {
    return Number(this.data?.cards?.[key] ?? 0);
  }

  percentOf(value: number, total: number): number {
    if (total <= 0 || value <= 0) {
      return 0;
    }

    return Math.min(100, Math.max(3, Math.round((value / total) * 100)));
  }

  percentText(value: number, total: number): string {
    if (total <= 0) {
      return '0%';
    }

    return `${Math.round((value / total) * 100)}%`;
  }

  formatNumber(value: number | string | null | undefined): string {
    const numeric = Number(value ?? 0);
    return this.numberFormatter.format(Number.isFinite(numeric) ? numeric : 0);
  }

  formatDateTime(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return 'Not set';
    }

    return this.dateTimeFormatter.format(date);
  }

  formatDateOnly(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return 'Not set';
    }

    return this.dateFormatter.format(date);
  }

  timeAgo(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return 'No activity';
    }

    const diffMs = Date.now() - date.getTime();
    const diffHours = Math.max(0, Math.floor(diffMs / 3600000));
    if (diffHours < 1) {
      return 'Just now';
    }
    if (diffHours < 48) {
      return `${diffHours}h ago`;
    }

    return `${Math.floor(diffHours / 24)}d ago`;
  }

  rowReference(row: DashboardRow): string {
    return row.alternate_reference || row.lading_number || row.trip_number || 'Unnumbered';
  }

  rowRoute(row: DashboardRow): string {
    const origin = [row.shipper_locality, row.shipper_region].filter(Boolean).join(', ');
    const destination = [row.consignee_locality, row.consignee_region].filter(Boolean).join(', ');
    if (origin && destination) {
      return `${origin} -> ${destination}`;
    }

    return origin || destination || 'Route pending';
  }

  statusTone(status: string | null | undefined): string {
    const value = (status ?? '').toLowerCase();
    if (value.includes('route') || value.includes('transit') || value.includes('shipped')) {
      return 'bg-sky-50 text-sky-700 ring-sky-200';
    }
    if (value.includes('arrived') || value.includes('loaded') || value.includes('delivery')) {
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    }
    if (value.includes('booked') || value.includes('qualify') || value.includes('unassigned')) {
      return 'bg-amber-50 text-amber-800 ring-amber-200';
    }

    return 'bg-slate-50 text-slate-700 ring-slate-200';
  }

  kpiToneClasses(tone: Tone): string {
    const classes: Record<Tone, string> = {
      blue: 'ops-kpi--blue',
      emerald: 'ops-kpi--emerald',
      amber: 'ops-kpi--amber',
      rose: 'ops-kpi--rose',
      violet: 'ops-kpi--violet',
      slate: 'ops-kpi--slate',
    };
    return classes[tone];
  }

  trackByKpi(_index: number, item: KpiCard): string {
    return item.key;
  }

  trackByStatus(_index: number, item: StatusCount): string {
    return item.lading_status;
  }

  trackByQueue(_index: number, item: DashboardRow): string {
    return item.lading_uuid || item.alternate_reference || String(_index);
  }

  trackByDimension(_index: number, item: DimensionRow): string {
    return item.uuid || item.name;
  }

  private buildParams(): HttpParams {
    let params = new HttpParams()
      .set('dashboard_view', this.filters.dashboard_view)
      .set('sections', 'summary,status,late,due,stale,dimensions')
      .set('date_basis', this.filters.date_basis)
      .set('date_start', this.filters.date_start)
      .set('date_end', this.filters.date_end)
      .set('active_only', String(this.filters.active_only))
      .set('include_cancelled', String(this.filters.include_cancelled))
      .set('stale_hours', String(this.filters.stale_hours))
      .set('detail_limit', String(this.filters.detail_limit))
      .set('timezone', this.filters.timezone || 'America/New_York');

    if (this.filters.lading_status !== 'all') {
      params = params.set('lading_status', this.filters.lading_status);
    }

    if (this.activeContactFilter) {
      params = params.set(this.activeContactFilter.paramName, this.activeContactFilter.uuid);
    }

    return params;
  }

  private handleResponse(response: LadingOperationsDashboardResponse | ApiMessage[]): void {
    if (Array.isArray(response)) {
      this.data = null;
      this.warnings = [];
      this.errorMessage = response[0]?.message || 'Dashboard API returned an error.';
      return;
    }

    if (!response?.success) {
      this.data = null;
      this.warnings = response?.warnings ?? [];
      this.errorMessage = 'Dashboard API did not return a successful response.';
      return;
    }

    this.data = response;
    this.warnings = response.warnings ?? [];
    this.syncFiltersFromResponse(response.filters);
  }

  private syncFiltersFromResponse(filters: DashboardFilters | undefined): void {
    if (!filters) {
      return;
    }

    this.filters.date_start = this.datePart(filters.date_start) || this.filters.date_start;
    this.filters.date_end = this.datePart(filters.date_end) || this.filters.date_end;
    this.filters.active_only = filters.active_only ?? this.filters.active_only;
    this.filters.include_cancelled = filters.include_cancelled ?? this.filters.include_cancelled;
    this.filters.stale_hours = Number(filters.stale_hours ?? this.filters.stale_hours);
    this.filters.detail_limit = Number(filters.detail_limit ?? this.filters.detail_limit);
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  private datePart(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value.slice(0, 10);
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private detectTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  }

  private resolveInitialTheme(): ThemeMode {
    const savedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }

    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
